import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  LayoutDashboard,
  Plug,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";
import { Section } from "@/components/Section";
import { SocialProof } from "@/components/SocialProof";
import { InlineEmailCapture } from "@/components/InlineEmailCapture";
import { StatsBar } from "@/components/StatsBar";
import { TestimonialCard } from "@/components/TestimonialCard";
import { allRoles } from "@/lib/roles";
import { BRAND, SITE_DESCRIPTION, SITE_URL } from "@/lib/site";
import { useWaitlist } from "@/components/WaitlistContext";
import { useInView } from "@/hooks/useInView";
import { trackEvent } from "@/lib/analytics";
import testimonialData from "@/data/testimonials.json";

interface Testimonial { quote: string; author: string; company: string }
const homepageTestimonials = (testimonialData as Record<string, Testimonial[]>).homepage ?? [];

const BENEFITS = [
  {
    icon: Zap,
    title: "Zero setup",
    body: "Pick your role. Your agent is already wired to the workflows you actually do — no prompt library to curate.",
  },
  {
    icon: LayoutDashboard,
    title: "Role-native workflows",
    body: "Every agent ships with 4–6 opinionated workflows tuned for that role's highest-leverage tasks.",
  },
  {
    icon: Plug,
    title: "Your tools, connected",
    body: "Gmail, Google Workspace, Slack, HubSpot, Notion, Zoom. One-click OAuth — no IT ticket needed.",
  },
  {
    icon: ShieldCheck,
    title: "Private by default",
    body: "SOC 2 Type II in flight. Your data stays yours — no training on your conversations, ever.",
  },
  {
    icon: Clock,
    title: "Live in under 10 minutes",
    body: "Connect one tool and run your first workflow inside your first coffee. We mean it.",
  },
  {
    icon: Sparkles,
    title: "Upgrades itself",
    body: "When the ecosystem learns a better way to do a task, your agent gets the improvement automatically.",
  },
];

const FEATURED_SLUGS = [
  "realtors",
  "recruiters",
  "sales-professionals",
  "customer-support",
  "operations-managers",
  "content-creators",
  "executive-assistants",
  "project-managers",
];

export function HomePage() {
  const roles = allRoles();
  const { openWaitlist } = useWaitlist();
  const featured = FEATURED_SLUGS
    .map((slug) => roles.find((r) => r.slug === slug))
    .filter((r): r is NonNullable<typeof r> => Boolean(r));

  const benefitsRef = useInView<HTMLUListElement>();
  const rolesRef = useInView<HTMLUListElement>();
  const stepsRef = useInView<HTMLOListElement>();
  const testimonialsRef = useInView<HTMLDivElement>();

  const organizationLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND,
    url: SITE_URL,
    sameAs: [],
    description: SITE_DESCRIPTION,
  };

  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/for?q={query}`,
      "query-input": "required name=query",
    },
  };

  return (
    <>
      <SEO
        title="Zero-setup, role-ready AI agents"
        description={SITE_DESCRIPTION}
        path="/"
        jsonLd={[organizationLd, websiteLd]}
      />

      {/* ---------------------------------------------------------------- HERO */}
      <section aria-labelledby="hero-heading" data-hero className="relative overflow-hidden">
        <div className="container py-20 sm:py-28 lg:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="coral" className="mb-5 animate-fade-in opacity-0">
              Agents that ship ready
            </Badge>
            <h1
              id="hero-heading"
              className="text-display-lg sm:text-display-xl font-display tracking-tight text-ink text-balance headline-two-tone animate-fade-in opacity-0 delay-100"
            >
              Pick your role. <em>Press go.</em>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground text-balance sm:text-xl animate-fade-in opacity-0 delay-200">
              Pre-built AI agents with role-native workflows. Connect one tool, run your first
              workflow in 10 minutes.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row animate-fade-in opacity-0 delay-300">
              <Button size="lg" onClick={() => { trackEvent("CTA_Click", { location: "hero_primary" }); openWaitlist(); }}>
                Start free trial <ArrowRight aria-hidden="true" />
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/vs/openclaw">Compare vs OpenClaw</Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground animate-fade-in opacity-0 delay-300">
              Free 14-day trial · No credit card · Live in 10 minutes
            </p>
            <SocialProof className="mt-8" />
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- WHY CONDUIT */}
      <Section
        tone="cream"
        eyebrow="Why Conduit"
        heading="Opinionated beats configurable"
        intro="Most AI tools hand you a blank chat and a prompt library. Conduit hands you a role and the five workflows that role lives in."
      >
        <ul
          ref={benefitsRef.ref}
          role="list"
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {BENEFITS.map(({ icon: Icon, title, body }, i) => (
            <li
              key={title}
              className={benefitsRef.isInView ? "animate-scale-fade-in" : "opacity-0"}
              style={{ animationDelay: `${i * 75}ms` }}
            >
              <Card className="h-full hover:-translate-y-0.5 hover:shadow-lg">
                <CardHeader>
                  <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-coral-50 text-coral-700">
                    <Icon aria-hidden="true" className="h-5 w-5" />
                  </div>
                  <CardTitle>{title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">{body}</CardDescription>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      </Section>

      {/* --------------------------------------------------------------- STATS */}
      <Section>
        <StatsBar />
      </Section>

      {/* ------------------------------------------------------------- BY ROLE */}
      <Section
        eyebrow="By role"
        heading={`Ready-made agents for ${roles.length}+ roles`}
        intro={`Each role ships with the workflows that role's best operators run every week. Browse a few — or see all ${roles.length}.`}
      >
        <ul
          ref={rolesRef.ref}
          role="list"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {featured.map((role, i) => (
            <li
              key={role.slug}
              className={rolesRef.isInView ? "animate-scale-fade-in" : "opacity-0"}
              style={{ animationDelay: `${i * 75}ms` }}
            >
              <Link
                to={`/for/${role.slug}`}
                className="group flex h-full flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-card transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div>
                  <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                    For
                  </p>
                  <p className="mt-1 text-xl font-semibold text-ink">{role.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                    {role.tagline}
                  </p>
                </div>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-coral-700 group-hover:text-coral-600">
                  See the agent
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-10 text-center">
          <Button variant="outline" asChild>
            <Link to="/for">Browse all {roles.length} roles</Link>
          </Button>
        </div>
      </Section>

      {/* ------------------------------------------------------- TESTIMONIALS */}
      {homepageTestimonials.length > 0 && (
        <Section
          tone="cream"
          eyebrow="What people say"
          heading="Teams already shipping with Conduit"
        >
          <div ref={testimonialsRef.ref} className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            {homepageTestimonials.map((t, i) => (
              <div
                key={i}
                className={testimonialsRef.isInView ? "animate-scale-fade-in" : "opacity-0"}
                style={{ animationDelay: `${i * 75}ms` }}
              >
                <TestimonialCard quote={t.quote} author={t.author} company={t.company} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* --------------------------------------------------------- HOW IT WORKS */}
      <Section
        tone="ink"
        eyebrow="How it works"
        heading="Three steps. About nine minutes."
      >
        <ol ref={stepsRef.ref} className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              n: "01",
              title: "Pick your role",
              body: "Choose from 50 pre-built agents, each with its own workflows and integrations.",
            },
            {
              n: "02",
              title: "Connect one tool",
              body: "Gmail, Slack, or your CRM. One OAuth click. The agent configures itself.",
            },
            {
              n: "03",
              title: "Run your first workflow",
              body: "No prompting needed. Pick a workflow, give it the context, watch it work.",
            },
          ].map((step, i) => (
            <li
              key={step.n}
              className={`rounded-xl border border-cream/15 bg-cream/5 p-6 text-cream ${stepsRef.isInView ? "animate-scale-fade-in" : "opacity-0"}`}
              style={{ animationDelay: `${i * 75}ms` }}
            >
              <p className="font-mono text-sm text-coral-400">{step.n}</p>
              <p className="mt-2 text-xl font-semibold">{step.title}</p>
              <p className="mt-2 text-cream/75">{step.body}</p>
            </li>
          ))}
        </ol>

        <div className="mt-12 text-center">
          <Button size="lg" variant="default" onClick={() => { trackEvent("CTA_Click", { location: "how_it_works" }); openWaitlist(); }}>
            Start free <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ CTA */}
      <Section tone="cream">
        <div className="mx-auto max-w-3xl rounded-2xl bg-ink p-10 text-center text-cream shadow-card sm:p-14">
          <h2 className="text-display-md font-display text-balance">
            Your agent is ready. Are you?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-cream/80">
            {roles.length} roles. One-click integrations. Opinionated workflows. Stop configuring and start
            shipping.
          </p>
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <InlineEmailCapture location="homepage_bottom" className="max-w-sm [&_input]:border-cream/30 [&_input]:bg-cream/10 [&_input]:text-cream [&_input]:placeholder-cream/50" />
          </div>
          <div className="mt-4">
            <Button size="lg" variant="outline" asChild className="border-cream/30 bg-transparent text-cream hover:bg-cream/10 hover:text-cream">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
